import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, User, Mail, CreditCard, Calendar, 
  Image, Gift, Edit2, Save, X
} from 'lucide-react';
import { adminFetch } from './AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const AdminUserDetail = () => {
  const { adminToken } = useOutletContext();
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [artworkCount, setArtworkCount] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [newCredits, setNewCredits] = useState(0);
  const [creditReason, setCreditReason] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await adminFetch(`/admin/users/${userId}`, {}, adminToken);
        setUser(data.user);
        setArtworkCount(data.artwork_count);
        setTransactions(data.recent_transactions || []);
        setNewCredits(data.user.credits || 0);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId, adminToken]);

  const handleUpdateCredits = async () => {
    try {
      await adminFetch(`/admin/users/${userId}/credits`, {
        method: 'PATCH',
        body: JSON.stringify({
          credits: newCredits,
          reason: creditReason || 'Admin adjustment'
        })
      }, adminToken);
      
      setUser(u => ({ ...u, credits: newCredits }));
      setEditing(false);
      toast.success('Credits updated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">User not found</p>
        <Link to="/admin/users" className="text-emerald-400 hover:underline mt-2 inline-block">
          Back to Users
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link 
        to="/admin/users" 
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft size={18} />
        Back to Users
      </Link>

      {/* User Header */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 bg-emerald-600/20 rounded-full flex items-center justify-center flex-shrink-0">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-20 h-20 rounded-full" />
            ) : (
              <User className="w-10 h-10 text-emerald-500" />
            )}
          </div>
          
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white">{user.name || 'Unknown User'}</h1>
            <div className="flex items-center gap-2 text-gray-400 mt-1">
              <Mail size={16} />
              {user.email}
            </div>
            <div className="flex items-center gap-2 text-gray-400 mt-1">
              <Calendar size={16} />
              Joined {new Date(user.created_at).toLocaleDateString()}
            </div>
          </div>

          <Link 
            to={`/admin/artworks?user_id=${userId}`}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
          >
            <Image size={18} />
            View Artworks ({artworkCount})
          </Link>
        </div>
      </div>

      {/* Credits Section */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <CreditCard size={20} />
            Credits Management
          </h2>
          {!editing && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setEditing(true)}
              className="border-gray-600"
            >
              <Edit2 size={16} className="mr-2" />
              Edit
            </Button>
          )}
        </div>

        {editing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Current Credits</label>
                <p className="text-2xl font-bold text-white">{user.credits || 0}</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">New Credits Value</label>
                <Input
                  type="number"
                  min="0"
                  value={newCredits}
                  onChange={(e) => setNewCredits(parseInt(e.target.value) || 0)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Reason</label>
              <Input
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                placeholder="e.g., Refund, Bonus, Correction"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setEditing(false);
                  setNewCredits(user.credits || 0);
                }}
                className="border-gray-600"
              >
                <X size={16} className="mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateCredits}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Save size={16} className="mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="px-6 py-4 bg-purple-600/20 rounded-xl">
              <p className="text-4xl font-bold text-purple-400">{user.credits || 0}</p>
              <p className="text-purple-300 text-sm">Available Credits</p>
            </div>
          </div>
        )}
      </div>

      {/* Recent Transactions */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Recent Transactions</h2>
        
        {transactions.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No transactions found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-sm text-gray-400">Date</th>
                  <th className="px-4 py-2 text-left text-sm text-gray-400">Package</th>
                  <th className="px-4 py-2 text-left text-sm text-gray-400">Amount</th>
                  <th className="px-4 py-2 text-left text-sm text-gray-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, i) => (
                  <tr key={i} className="border-b border-gray-700/50">
                    <td className="px-4 py-3 text-gray-300">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-white">{tx.package_id || 'N/A'}</td>
                    <td className="px-4 py-3 text-white">${tx.amount?.toFixed(2) || '0.00'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        tx.payment_status === 'paid' 
                          ? 'bg-green-600/20 text-green-400'
                          : 'bg-yellow-600/20 text-yellow-400'
                      }`}>
                        {tx.payment_status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserDetail;
